import React, { useEffect, useState } from "react";
import "./QuestionSets.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faArrowLeft,
  faTrashAlt,
  faEye,
  faEllipsisV,
  faGripVertical,
  faSave,
  faEdit,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "./Modal";
import {
  fetchQuestionSets,
  createQuestionSet,
  updateQuestionSet,
  deleteQuestionSet,
} from "../utils/api";
import { formatYyyyDdMm } from "../utils/date";
import PageHeader from "./layout/PageHeader";
import { ListChecks } from "lucide-react";

const QUESTION_TYPES = [
  { id: "single", label: "Single Choice" },
  { id: "multiple", label: "Multiple Choice" },
  { id: "text", label: "Text Input" },
  { id: "yesno", label: "Yes/No" },
];

const QuestionSets = ({
  activeLocation,
  locations = [],
  onChangeLocation,
  companyDisplayName,
  currentUser,
  onLogout,
  onLock,
}) => {
  const [view, setView] = useState("list"); // 'list' or 'create'
  const [sets, setSets] = useState([]);
  const [filterType, setFilterType] = useState("All Types");
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);

  const formatTypeLabel = (type) => {
    if (type === "CHECK_IN") return "Check-in";
    if (type === "CHECK_OUT") return "Check-out";
    return type || "Unknown";
  };

  const blankQuestion = () => ({
    id: Date.now() + Math.random(),
    text: "",
    type: "single",
    options: ["Option 1", "Option 2"],
    required: true,
  });

  // Form State
  const [formTitle, setFormTitle] = useState("");
  const [formType, setFormType] = useState("Check-in");
  const [questions, setQuestions] = useState([blankQuestion()]);
  const [editSetId, setEditSetId] = useState(null);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [previewSet, setPreviewSet] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const filteredSets = sets.filter((s) => {
    if (filterType === "All Types") return true;
    return formatTypeLabel(s.type) === filterType;
  });

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchQuestionSets();
        setSets(data || []);
      } catch (err) {
        console.error(err);
      }
    };
    load();
  }, []);

  const handleAddQuestion = () => {
    setQuestions([...questions, blankQuestion()]);
  };

  const handleRemoveQuestion = (id) => {
    setQuestions(questions.filter((q) => q.id !== id));
  };

  const resetFormState = () => {
    setFormTitle("");
    setFormType("Check-in");
    setQuestions([blankQuestion()]);
    setEditSetId(null);
  };

  const handleEditSet = (set) => {
    const typeLabel = set.type === "CHECK_IN" ? "Check-in" : "Check-out";
    const mappedQuestions =
      (set.questions || []).map((q, idx) => ({
        id: `${set._id}-${idx}-${Date.now()}`,
        text: q.text,
        type: q.type,
        options: q.options || [],
        required: q.required ?? true,
      })) || [blankQuestion()];
    setFormTitle(set.title);
    setFormType(typeLabel);
    setQuestions(mappedQuestions.length ? mappedQuestions : [blankQuestion()]);
    setEditSetId(set._id);
    setView("create");
    setMenuOpenId(null);
  };

  const handleDeleteSet = (set) => {
    setDeleteTarget(set);
    setIsDeleteModalOpen(true);
    setMenuOpenId(null);
  };

  const handleDuplicateSet = async (set) => {
    try {
      const payload = {
        title: `${set.title} (Copy)`,
        type: set.type,
        questions: (set.questions || []).map((q) => ({
          text: q.text,
          type: q.type,
          options: q.options || [],
          required: q.required ?? true,
        })),
      };
      const created = await createQuestionSet(payload);
      setSets((prev) => [created, ...prev]);
    } catch (err) {
      console.error("Failed to duplicate question set", err);
    } finally {
      setMenuOpenId(null);
    }
  };

  const confirmDeleteSet = async () => {
    if (!deleteTarget) return;
    try {
      await deleteQuestionSet(deleteTarget._id || deleteTarget.id);
      setSets((prev) =>
        prev.filter(
          (set) =>
            set._id !== deleteTarget._id &&
            set.id !== (deleteTarget.id || deleteTarget._id)
        )
      );
    } catch (err) {
      console.error("Failed to delete question set", err);
    } finally {
      setIsDeleteModalOpen(false);
      setDeleteTarget(null);
    }
  };

  const handlePreviewSet = (set) => {
    setPreviewSet(set);
    setIsPreviewOpen(true);
  };

  const updateQuestion = (id, field, value) => {
    setQuestions(
      questions.map((q) => (q.id === id ? { ...q, [field]: value } : q))
    );
  };

  const handleAddOption = (qId) => {
    const q = questions.find((x) => x.id === qId);
    if (q) {
      updateQuestion(qId, "options", [
        ...q.options,
        `Option ${q.options.length + 1}`,
      ]);
    }
  };

  const handleRemoveOption = (qId, idx) => {
    const q = questions.find((x) => x.id === qId);
    if (q) {
      const newOpts = q.options.filter((_, i) => i !== idx);
      updateQuestion(qId, "options", newOpts);
    }
  };

  const handleOptionChange = (qId, idx, val) => {
    const q = questions.find((x) => x.id === qId);
    if (q) {
      const newOpts = [...q.options];
      newOpts[idx] = val;
      updateQuestion(qId, "options", newOpts);
    }
  };

  const handleSaveSet = async () => {
    if (!formTitle.trim()) return;
    const payload = {
      title: formTitle,
      type: formType === "Check-in" ? "CHECK_IN" : "CHECK_OUT",
      questions: questions.map((q) => ({
        text: q.text,
        type: q.type,
        options: q.options,
        required: q.required,
      })),
    };
    try {
      let saved;
      if (editSetId) {
        saved = await updateQuestionSet(editSetId, payload);
        setSets((prev) =>
          prev.map((set) => (set._id === editSetId ? saved : set))
        );
      } else {
        saved = await createQuestionSet(payload);
        setSets([saved, ...sets]);
      }
    } catch (err) {
      console.error(err);
    }
    setView("list");
    resetFormState();
    setMenuOpenId(null);
  };

  if (view === "create") {
    return (
      <div className="qs-container page-shell">
        <PageHeader
          title="Question Sets"
          subtitle="Design reusable assessments for check-in and check-out flows."
          icon={<ListChecks className="h-6 w-6 text-white" />}
          activeLocation={activeLocation}
          locations={locations}
          onChangeLocation={onChangeLocation}
          companyDisplayName={companyDisplayName}
          currentUser={currentUser}
          onLogout={onLogout}
          onLock={onLock}
        />
        <div className="qs-header">
          <div className="qs-header-left">
            <button
              className="back-btn"
              onClick={() => {
                setView("list");
                resetFormState();
              }}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <div className="qs-header-text">
              <div className="qs-title">
                {editSetId ? "Edit Question Set" : "Create Question Set"}
              </div>
              <p className="qs-subtitle">
                Build a reusable assessment for appointments
              </p>
            </div>
          </div>
          <button className="primary-btn" onClick={handleSaveSet}>
            <FontAwesomeIcon icon={faSave} />{" "}
            {editSetId ? "Save Changes" : "Save Question Set"}
          </button>
        </div>

        <div className="qs-form-content">
          <div className="qs-section">
            <h3 className="section-title">BASIC INFORMATION</h3>
            <div className="form-group">
              <label>Question Set Title</label>
              <input
                type="text"
                placeholder="e.g., Standard Check-in Assessment"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                className="qs-input"
              />
            </div>
            <div className="form-group">
              <label>Question Set Type</label>
              <div className="type-toggles">
                <button
                  className={`type-toggle check-in ${
                    formType === "Check-in" ? "active" : ""
                  }`}
                  onClick={() => setFormType("Check-in")}
                >
                  <div className="radio-circle">
                    {formType === "Check-in" && <div className="dot" />}
                  </div>
                  Check-in
                </button>
                <button
                  className={`type-toggle check-out ${
                    formType === "Check-out" ? "active" : ""
                  }`}
                  onClick={() => setFormType("Check-out")}
                >
                  <div className="radio-circle">
                    {formType === "Check-out" && <div className="dot" />}
                  </div>
                  Check-out
                </button>
              </div>
            </div>
          </div>

          <div className="qs-section questions-section">
            <div className="qs-section-head">
              <h3 className="section-title">QUESTIONS ({questions.length})</h3>
              <button className="add-q-btn" onClick={handleAddQuestion}>
                <FontAwesomeIcon icon={faPlus} /> Add Question
              </button>
            </div>

            <div className="questions-list">
              {questions.map((q, index) => (
                <div key={q.id} className="question-card">
                  <div className="q-card-header">
                    <div className="drag-handle">
                      <FontAwesomeIcon icon={faGripVertical} />
                    </div>
                    <div className="q-number-circle">{index + 1}</div>
                    <div className="q-actions">
                      <button
                        className="delete-q-btn"
                        onClick={() => handleRemoveQuestion(q.id)}
                      >
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </button>
                    </div>
                  </div>

                  <div className="q-card-body">
                    <div className="form-group">
                      <input
                        type="text"
                        placeholder="Question Text"
                        value={q.text}
                        onChange={(e) =>
                          updateQuestion(q.id, "text", e.target.value)
                        }
                        className="qs-input"
                      />
                    </div>

                    <div className="form-group">
                      <label>Question Type</label>
                      <div className="q-type-options">
                        {QUESTION_TYPES.map((type) => (
                          <button
                            key={type.id}
                            className={`q-type-btn ${
                              q.type === type.id ? "active" : ""
                            }`}
                            onClick={() =>
                              updateQuestion(q.id, "type", type.id)
                            }
                          >
                            <div className="radio-circle">
                              {q.type === type.id && <div className="dot" />}
                            </div>
                            {type.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {(q.type === "single" || q.type === "multiple") && (
                      <div className="form-group">
                        <label>Options</label>
                        <div className="options-list">
                          {q.options.map((opt, idx) => (
                            <div key={idx} className="option-row">
                              <span className="opt-idx">{idx + 1}</span>
                              <input
                                type="text"
                                value={opt}
                                onChange={(e) =>
                                  handleOptionChange(q.id, idx, e.target.value)
                                }
                                className="qs-input"
                              />
                              <button
                                className="delete-opt-btn"
                                onClick={() => handleRemoveOption(q.id, idx)}
                              >
                                <FontAwesomeIcon icon={faTrashAlt} />
                              </button>
                            </div>
                          ))}
                          <button
                            className="add-opt-link"
                            onClick={() => handleAddOption(q.id)}
                          >
                            <FontAwesomeIcon icon={faPlus} /> Add Option
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="q-footer">
                      <button
                        className={`req-toggle ${q.required ? "active" : ""}`}
                        onClick={() =>
                          updateQuestion(q.id, "required", !q.required)
                        }
                      >
                        <div className="radio-circle">
                          {q.required && <div className="dot" />}
                        </div>
                        Required Question
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="qs-container page-shell">
      <PageHeader
        title="Question Sets"
        subtitle="Design reusable assessments for check-in and check-out flows."
        icon={<ListChecks className="h-6 w-6 text-white" />}
        activeLocation={activeLocation}
        locations={locations}
        onChangeLocation={onChangeLocation}
        companyDisplayName={companyDisplayName}
        currentUser={currentUser}
        onLogout={onLogout}
        onLock={onLock}
      />
      <div className="page-actions">
        <button className="primary-btn" onClick={() => setView("create")}>
          <FontAwesomeIcon icon={faPlus} /> New Question Set
        </button>
      </div>

      <div className="qs-filters">
        <div className="type-filter-wrapper">
          <label className="filter-label">Type</label>
          <div
            className="type-dropdown-trigger"
            onClick={() => setIsTypeDropdownOpen(!isTypeDropdownOpen)}
          >
            {filterType === "All Types" ? (
              <span className="type-all">All Types</span>
            ) : filterType === "Check-in" ? (
              <span className="type-badge check-in">
                <div className="dot" /> Check-in
              </span>
            ) : (
              <span className="type-badge check-out">
                <div className="dot" /> Check-out
              </span>
            )}
            <FontAwesomeIcon icon={faEllipsisV} className="caret-icon" />
          </div>
          {isTypeDropdownOpen && (
            <div className="type-dropdown-menu">
              <div
                className="type-item"
                onClick={() => {
                  setFilterType("All Types");
                  setIsTypeDropdownOpen(false);
                }}
              >
                All Types
              </div>
              <div
                className="type-item"
                onClick={() => {
                  setFilterType("Check-in");
                  setIsTypeDropdownOpen(false);
                }}
              >
                <span className="type-dot green" /> Check-in
              </div>
              <div
                className="type-item"
                onClick={() => {
        setFilterType("Check-out");
                  setIsTypeDropdownOpen(false);
                }}
              >
                <span className="type-dot purple" /> Check-out
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="qs-table-card">
        <div className="qs-table-head">
          <div>Title</div>
          <div>Type</div>
          <div>Questions</div>
          <div>Created</div>
          <div>Actions</div>
        </div>
        {filteredSets.map((set) => {
          const typeLabel = formatTypeLabel(set.type);
          const questionCount = set.questions?.length || 0;
          const createdLabel = set.createdAt ? formatYyyyDdMm(set.createdAt) : "-";
          return (
            <div key={set._id || set.id} className="qs-table-row">
              <div className="qs-title-cell">{set.title}</div>
              <div>
                <span
                  className={`type-badge ${
                    typeLabel === "Check-in" ? "check-in" : "check-out"
                  }`}
                >
                  {typeLabel}
                </span>
              </div>
              <div className="qs-q-count">{questionCount} questions</div>
              <div className="qs-date">{createdLabel}</div>
              <div className="qs-actions-cell">
                <div className="qs-actions">
                  <button
                    className="icon-btn blue"
                    onClick={() => handlePreviewSet(set)}
                  >
                    <FontAwesomeIcon icon={faEye} />
                  </button>
                  <div className="actions-menu-wrapper">
                    <button
                      className="icon-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpenId(menuOpenId === set._id ? null : set._id);
                      }}
                    >
                      <FontAwesomeIcon icon={faEllipsisV} />
                    </button>
                    {menuOpenId === set._id && (
                      <div
                        className="qs-actions-menu"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          className="action-item"
                          onClick={() => handleEditSet(set)}
                        >
                          <FontAwesomeIcon icon={faEdit} /> Edit
                        </button>
                        <button
                          type="button"
                          className="action-item"
                          onClick={() => handleDuplicateSet(set)}
                        >
                          <FontAwesomeIcon icon={faCopy} /> Duplicate
                        </button>
                        <button
                          type="button"
                          className="action-item delete"
                          onClick={() => handleDeleteSet(set)}
                        >
                          <FontAwesomeIcon icon={faTrashAlt} /> Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {isDeleteModalOpen && (
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          title="Delete Question Set"
        >
          <p>
            This action cannot be undone. Are you sure you want to delete "
            {deleteTarget?.title}"?
          </p>
          <div className="modal-actions">
            <button
              type="button"
              className="button cancel-button"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="button delete-button"
              onClick={confirmDeleteSet}
            >
              Delete
            </button>
          </div>
        </Modal>
      )}
      {isPreviewOpen && previewSet && (
        <Modal
          isOpen={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          title={`Preview: ${previewSet.title}`}
        >
          <div className="preview-meta">
            <span className="type-badge">
              {formatTypeLabel(previewSet.type)}
            </span>
            <span className="qs-q-count">
              {previewSet.questions?.length || 0} questions
            </span>
          </div>
          <div className="preview-questions">
            {(previewSet.questions || []).map((q, idx) => (
              <div key={idx} className="preview-question-card">
                <div className="preview-q-head">
                  <span className="q-number-circle">{idx + 1}</span>
                  <span className="q-text">{q.text}</span>
                  <span className="q-type-chip">{q.type}</span>
                  {q.required && <span className="q-required">Required</span>}
                </div>
                {(q.type === "single" || q.type === "multiple") &&
                  (q.options || []).length > 0 && (
                    <ul className="q-options-list">
                      {q.options.map((opt, i) => (
                        <li key={i}>{opt}</li>
                      ))}
                    </ul>
                  )}
              </div>
            ))}
          </div>
          <div className="modal-actions" style={{ marginTop: "12px" }}>
            <button
              type="button"
              className="button cancel-button"
              onClick={() => setIsPreviewOpen(false)}
            >
              Close
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default QuestionSets;
